/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Row, Col } from "react-awesome-styled-grid";
import styled from "styled-components";
import { Box, FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import { formatMoney } from "~/tools";

type BorderBoxProps = {
  border?: boolean;
};

export const IconWrapper = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.color.background.ui03};
  border-radius: ${theme.radiusDefault};
  margin: ${theme.spacing.spacing01} ${theme.spacing.spacing02} 0 0;
`;

const BorderBox = styled.div<BorderBoxProps>`
  padding: ${theme.spacing.spacing04} ${theme.spacing.spacing03};
  margin-bottom: ${({ border = true }) =>
    `${border ? theme.spacing.spacing04 : theme.spacing.spacing00}`};
  border-bottom: 1px solid
    ${({ border = true }) =>
      `${border ? theme.color.border.border01 : "transparent"}`};
`;

const CostWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${theme.spacing.spacing01} 0;
`;

type Props = {
  currentMonth: string;
  previousMonth?: string;
  forecast?: string;
};

export function Cost({ currentMonth, previousMonth, forecast }: Props) {
  const { currency } = useCurrency();

  return (
    <Box color={theme.color.background.ui03} boxStyle="lightGrey">
      <BorderBox border={false}>
        <Row>
          <Col>
            <Text isTitle size="sm" tag="span">
              Cost
            </Text>
          </Col>
          <Col style={{ textAlign: "right" }}>
            <Text isTitle size="sm" tag="span">
              {periodDayJs().format(dateFormat.monthYear)}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Text tag="div" size="xl" weight="medium">
              <FlexContainer justifyContent="start">
                {formatMoney(currentMonth, currency)}
              </FlexContainer>
            </Text>
          </Col>
        </Row>
      </BorderBox>
      <Col>
        <Row>
          {previousMonth && (
            <Col sm={4}>
              <Row>
                <IconWrapper>
                  <SVGIcon name="budget" />
                </IconWrapper>
                <CostWrap>
                  <Text isTitle size="xs" tag="span">
                    Previous Month
                  </Text>
                  <Text tag="div" weight="medium">
                    {formatMoney(previousMonth, currency)}
                  </Text>
                </CostWrap>
              </Row>
            </Col>
          )}
          {forecast && (
            <Col sm={4}>
              <Row>
                <IconWrapper>
                  <SVGIcon name="forecastUp" color="danger" />
                </IconWrapper>
                <CostWrap>
                  <Text isTitle size="xs" tag="span">
                    Monthly Forecast
                  </Text>
                  <Text tag="div" weight="medium">
                    {formatMoney(forecast, currency)}
                  </Text>
                </CostWrap>
              </Row>
            </Col>
          )}
        </Row>
      </Col>
    </Box>
  );
}
